import { ActionReducerMapBuilder } from "@reduxjs/toolkit";
import { noteStateType } from "../types";
import {
  createQuickProject,
  getProjects,
  getProject,
  deleteProject,
  updateProject,
  updateNotePositionsInProject,
  addTaskToProject,
  removeTaskFromProject,
  createCollectionInProject,
  deleteCollectionInProject,
  updateCollectionInProject,
  updateColumnPositionsInProject,
  deleteProjectAccess,
  createProjectAccess,
  getProjectTasks,
  updateProjectAccess,
  createProjectMilestone,
  updateProjectMilestone,
  updateProjectStatus,
  deleteProjectMilestone,
} from "./project.thunk";
import { getProjectCriticality } from "../../../helpers/projectStatus";
import { refreshUrgency } from "../../../helpers/effectiveDueDate";

export function addProjectReducers(
  builder: ActionReducerMapBuilder<noteStateType>,
) {
  builder
    .addCase(createQuickProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(createQuickProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      if (state.projects) {
        state.projects.push(action.payload);
      } else {
        state.projects = [action.payload];
      }
    })
    .addCase(createQuickProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    .addCase(getProjects.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(getProjects.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.projects = action.payload;
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    .addCase(getProjects.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    // Get project by ID
    .addCase(getProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(getProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      const proj = action.payload;

      state.project = {
        ...proj,
        ...getProjectCriticality(proj),
      };
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    .addCase(getProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    .addCase(deleteProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(deleteProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.projects = state.projects?.filter(
        (project) => project.id !== action.payload,
      );
    })
    .addCase(deleteProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    .addCase(updateProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(updateProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.projects = state.projects?.map((project) =>
        project.id !== action.payload.id
          ? project
          : { ...project, ...action.payload },
      );

      if (state.project) {
        state.project = { ...state.project, ...action.payload };
      }
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    .addCase(updateProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    .addCase(updateColumnPositionsInProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(updateColumnPositionsInProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      const { updatedColumns, projectId } = action.payload;

      if (state.project?.id !== projectId) return;
      if (!state.project.Collections) return;

      const positionMap = new Map(
        updatedColumns.map((c) => [c.collectionId, c.positionInProject]),
      );

      state.project.Collections = state.project.Collections.map((coll) =>
        positionMap.has(coll.id)
          ? { ...coll, positionInProject: positionMap.get(coll.id)! }
          : coll,
      );
    })
    .addCase(updateColumnPositionsInProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })

    .addCase(updateCollectionInProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(updateCollectionInProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      // Update the collection
      if (state.project?.Collections) {
        state.project.Collections = state.project.Collections.map((coll) =>
          coll.id === action.payload.id ? { ...coll, ...action.payload } : coll,
        );
      }

      // Update notes' storeStatus if collection storeStatus changed
      if (
        action.payload.storeStatus !== undefined &&
        action.payload.storeStatus !== null
      ) {
        const affectedNoteIds = new Set(
          state.project?.Collections?.find(
            (c) => c.id === action.payload.id,
          )?.notes?.map((n) => n.noteId) ?? [],
        );

        state.tasks = state.tasks.map((task) =>
          affectedNoteIds.has(task.id)
            ? { ...task, storeStatus: action.payload.storeStatus }
            : task,
        );
      }
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    .addCase(updateCollectionInProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })

    .addCase(updateNotePositionsInProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(updateNotePositionsInProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      const { projectId, updatedNotes } = action.payload;

      if (state.project?.id !== projectId) return;

      const allCollections = [
        ...(state.project.Collections ?? []),
        ...(state.project.inboxCollection
          ? [state.project.inboxCollection]
          : []),
      ];

      updatedNotes.forEach((note) => {
        // Remove from whichever collection currently holds it

        state.tasks = [...state.tasks].map((ta) =>
          ta.id === note.taskId ? { ...ta, ...note } : ta,
        );

        allCollections.forEach((col) => {
          col.notes = col.notes?.filter((n) => n.noteId !== note.taskId);
        });

        // Insert into the target collection
        const targetCol = allCollections.find(
          (col) => col.id === note.collectionId,
        );

        if (targetCol?.notes) {
          targetCol.notes.push({
            noteId: note.taskId,
            positionInList: note.position,
            collectionId: note.collectionId,
          });
        } else if (targetCol) {
          targetCol.notes = [
            {
              noteId: note.taskId,
              positionInList: note.position,
              collectionId: note.collectionId,
            },
          ];
        }
      });
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    .addCase(updateNotePositionsInProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })

    .addCase(addTaskToProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(addTaskToProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.project?.inboxCollection?.notes?.push(action.payload.task);
      if (state.project) {
        state.project = {
          ...state.project,
          unfinishedNotes: state.project.unfinishedNotes + 1,
        };
        state.project = {
          ...state.project,
          ...getProjectCriticality(state.project),
        };
      }
      if (state.projects) {
        state.projects = [...state.projects]?.map((proj) => {
          if (proj.id === action.payload.projectId) {
            return {
              ...proj,
              unfinishedNotes: proj.unfinishedNotes + 1,
              // New tasks land in the inbox; the task page reads this list
              inboxCollection: proj.inboxCollection && {
                ...proj.inboxCollection,
                notes: [
                  ...(proj.inboxCollection.notes || []),
                  action.payload.task,
                ],
              },
            };
          }
          return proj;
        });
      }
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    .addCase(addTaskToProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    .addCase(removeTaskFromProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })

    .addCase(removeTaskFromProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      const { taskId, collectionId, projectId } = action.payload;
      // Regular collections
      if (state.project?.Collections) {
        state.project.Collections = state.project.Collections.map((col) =>
          col.id !== collectionId
            ? col
            : { ...col, notes: col.notes?.filter((n) => n.noteId !== taskId) },
        );
      }

      if (state.project) {
        state.project = {
          ...state.project,
          unfinishedNotes: state.project.unfinishedNotes - 1,
        };
        state.project = {
          ...state.project,
          ...getProjectCriticality(state.project),
        };
      }

      // Inbox collection
      if (state.project?.inboxCollection?.id === collectionId) {
        state.project.inboxCollection.notes =
          state.project.inboxCollection.notes?.filter(
            (n) => n.noteId !== taskId,
          );
      }

      // Update project list count and the task's collection membership
      if (state.projects) {
        const withoutTask = <
          T extends { id: string; notes?: { noteId: string }[] },
        >(
          col: T,
        ): T =>
          col.id !== collectionId
            ? col
            : { ...col, notes: col.notes?.filter((n) => n.noteId !== taskId) };

        state.projects = state.projects.map((proj) =>
          proj.id === projectId
            ? {
                ...proj,
                unfinishedNotes: proj.unfinishedNotes - 1,
                inboxCollection:
                  proj.inboxCollection && withoutTask(proj.inboxCollection),
                Collections: proj.Collections?.map(withoutTask),
              }
            : proj,
        );
      }
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    .addCase(removeTaskFromProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    .addCase(createCollectionInProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(createCollectionInProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.project?.Collections?.push(action.payload);
    })
    .addCase(createCollectionInProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    .addCase(deleteCollectionInProject.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(deleteCollectionInProject.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      const deletedCollectionId = action.payload; // Assuming this returns the ID
      if (!state.project) return;

      // 1. Find the collection to be deleted
      const collectionToDelete = state.project.Collections?.find(
        (c) => c.id === deletedCollectionId,
      );

      if (collectionToDelete) {
        // 2. Extract the notes
        const notesToMove = collectionToDelete.notes || [];

        // 3. Get current max position in Inbox to avoid overlapping positions
        const currentInboxNotes = state.project.inboxCollection?.notes || [];
        const startPosition = currentInboxNotes.length;

        if (!state.project.inboxCollection) return;

        // 4. Update the notes' metadata (collectionId and position)
        const remappedNotes = notesToMove.map((note, index) => ({
          ...note,
          // @ts-ignore
          collectionId: state.project.inboxCollection.id,
          positionInList: startPosition + index,
        }));

        // 5. Add notes to Inbox
        if (state.project.inboxCollection) {
          state.project.inboxCollection.notes = [
            ...currentInboxNotes,
            ...remappedNotes,
          ];
        }

        // 6. Finally, remove the collection from the list
        state.project.Collections = state.project.Collections?.filter(
          (c) => c.id !== deletedCollectionId,
        );
      }
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    .addCase(deleteCollectionInProject.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })

    .addCase(createProjectAccess.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(createProjectAccess.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.project?.ProjectAccess.push(action.payload);

      // The list knows group/organization shares too ("Projekte" tab of a role)
      const { projectId, groupId, organizationId } = action.payload;
      const listed = state.projects?.find((p) => p.id === projectId);
      if (listed && (groupId || organizationId)) {
        listed.ProjectAccess = [
          ...(listed.ProjectAccess ?? []),
          { groupId: groupId ?? null, organizationId: organizationId ?? null },
        ];
      }
    })
    .addCase(createProjectAccess.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })
    .addCase(deleteProjectAccess.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    // Status dates; the status itself is derived from them (projectStatus.ts)
    .addCase(updateProjectStatus.fulfilled, (state, action) => {
      const { projectId, completedAt, cancelledAt } = action.payload;
      const withStatus = <P extends { id: string }>(p: P) =>
        p.id === projectId
          ? {
              ...p,
              completedAt,
              cancelledAt,
              ...getProjectCriticality({
                ...p,
                completedAt,
                cancelledAt,
              } as any),
            }
          : p;
      if (state.project) state.project = withStatus(state.project);
      if (state.projects) state.projects = state.projects.map(withStatus);
      // List/project deadlines set the least urgency of their tasks
      refreshUrgency(state);
    })
    // Milestones, kept sorted by date like the backend returns them
    .addCase(createProjectMilestone.fulfilled, (state, action) => {
      const { projectId, milestone } = action.payload;
      if (state.project?.id !== projectId) return;
      state.project.milestones = [
        ...(state.project.milestones ?? []),
        milestone,
      ].sort((a, b) => a.scheduleStart.localeCompare(b.scheduleStart));
    })
    .addCase(updateProjectMilestone.fulfilled, (state, action) => {
      const { projectId, milestone } = action.payload;
      if (state.project?.id !== projectId) return;
      state.project.milestones = (state.project.milestones ?? [])
        .map((m) => (m.id === milestone.id ? milestone : m))
        .sort((a, b) => a.scheduleStart.localeCompare(b.scheduleStart));
    })
    .addCase(deleteProjectMilestone.fulfilled, (state, action) => {
      const { projectId, id } = action.payload;
      if (state.project?.id !== projectId) return;
      state.project.milestones = (state.project.milestones ?? []).filter(
        (m) => m.id !== id,
      );
    })
    .addCase(deleteProjectAccess.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      console.log(action.payload);

      // Also drop it from the list's shares, matched by group/organization
      const removed = state.project?.ProjectAccess?.find(
        (acc) => acc.id === action.payload.accessId,
      );
      const listed = state.projects?.find(
        (p) => p.id === action.payload.projectId,
      );
      if (removed && listed?.ProjectAccess) {
        listed.ProjectAccess = listed.ProjectAccess.filter(
          (acc) =>
            !(
              (removed.groupId && acc.groupId === removed.groupId) ||
              (removed.organizationId &&
                acc.organizationId === removed.organizationId)
            ),
        );
      }

      if (state.project?.ProjectAccess)
        state.project.ProjectAccess = state.project?.ProjectAccess.filter(
          (acc) => acc.id !== action.payload.accessId,
        );
    })
    .addCase(deleteProjectAccess.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })

    .addCase(getProjectTasks.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(getProjectTasks.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;
      state.projectTasks = [...action.payload];
    })
    .addCase(getProjectTasks.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    })

    .addCase(updateProjectAccess.pending, (state) => {
      state.isLoading = true;
      state.isSuccess = false;
    })
    .addCase(updateProjectAccess.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isSuccess = true;

      console.log(action.payload);
      if (state.project?.ProjectAccess)
        state.project.ProjectAccess = state.project.ProjectAccess.map((acc) =>
          acc.id === action.payload.id ? { ...acc, ...action.payload } : acc,
        );
    })
    .addCase(updateProjectAccess.rejected, (state, action) => {
      state.isLoading = false;
      state.isError = true;
      state.message = action.payload as string;
    });

  // ...more cases
}
